import { Component } from '@verndale/core';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

class ProductImagesCarousel extends Component {
  swiperInstance: Swiper | undefined = undefined;

  constructor(el: HTMLElement) {
    super(el);

    this.initSwiper();
  }

  setupDefaults() {
    this.dom = {
      $swiper: this.el.querySelector<HTMLElement>('.swiper'),
      $swiperWrapper: this.el.querySelector<HTMLElement>('.swiper-wrapper'),
      $slides: this.el.querySelectorAll<HTMLElement>('.swiper-slide'),
      $prevBtn: this.el.querySelector<HTMLElement>('.product-images-carousel__prev-btn'),
      $nextBtn: this.el.querySelector<HTMLElement>('.product-images-carousel__next-btn'),
      $images: this.el.querySelectorAll<HTMLElement>('.product-images-carousel__image')
    };
  }

  addListeners() {
    this.el.addEventListener('keydown', this.handleKeyDown.bind(this));
    (this.dom.$prevBtn as HTMLElement).addEventListener('click', this.handlePrevBtnClick.bind(this));
    (this.dom.$nextBtn as HTMLElement).addEventListener('click', this.handleNextBtnClick.bind(this));
  }

  getSwiperProps() {
    const activeIndex = this.swiperInstance?.activeIndex as number;
    const totalSlides = this.swiperInstance?.slides.length as number;
    const firstVisible = this.swiperInstance?.slides[activeIndex] as HTMLElement;
    const lastVisible = this.swiperInstance?.slides[activeIndex + 3] as HTMLElement;

    return { totalSlides, activeIndex, firstVisible, lastVisible };
  }

  handleKeyDown(e: Event) {
    const ke = e as KeyboardEvent;
    const activeElement = document.activeElement as HTMLElement;
    const prevBtn = this.dom.$prevBtn as HTMLElement;
    const nextBtn = this.dom.$nextBtn as HTMLElement;
    const isTab = ke.key === 'Tab' && !ke.shiftKey;
    const isShiftTab = ke.key === 'Tab' && ke.shiftKey;
    const { firstVisible, lastVisible } = this.getSwiperProps();

    if (activeElement === prevBtn && isTab) {
      ke.preventDefault();
      firstVisible.focus();
      return;
    }

    if (activeElement === firstVisible && isShiftTab) {


      if (!prevBtn.getAttribute('disabled')) {
        ke.preventDefault();
        prevBtn.focus();
      }

      return;
    }

    if (activeElement === lastVisible && isTab) {
      ke.preventDefault();
      nextBtn.focus();
      return;
    }

    if (activeElement === nextBtn && isShiftTab) {
      ke.preventDefault();
      lastVisible.focus();
      return;
    }
  }

  handlePrevBtnClick() {
    const { activeIndex } = this.getSwiperProps();
    let targetSlide = activeIndex - 4;
    if (targetSlide < 0) targetSlide = 0;
    this.swiperInstance?.slideTo(targetSlide);
  }

  handleNextBtnClick() {
    const { totalSlides, activeIndex } = this.getSwiperProps();
    let targetSlide = activeIndex + 4;
    if (targetSlide >= totalSlides - 1)  targetSlide = totalSlides - 4;
    this.swiperInstance?.slideTo(targetSlide);
  }

  handleSlideChangeEnd() {
    const { totalSlides, activeIndex } = this.getSwiperProps();
    const isFirst = activeIndex === 0;
    const isLast = activeIndex === totalSlides - 4;
    const prevBtn = this.dom.$prevBtn as HTMLElement;
    const nextBtn = this.dom.$nextBtn as HTMLElement;

    prevBtn.setAttribute('aria-disabled', isFirst.toString());
    isFirst ? prevBtn.setAttribute('disabled', isFirst.toString()) : prevBtn.removeAttribute('disabled');

    nextBtn.setAttribute('aria-disabled', isLast.toString());
    isLast ? nextBtn.setAttribute('disabled', isLast.toString()) : nextBtn.removeAttribute('disabled');
  }

  handleSlideClick(swiper: Swiper) {
    this.setActiveSlide(swiper.clickedIndex);
  }

  initSwiper() {
    const totalSlides = (this.dom.$slides as NodeListOf<HTMLElement>).length as number;

    if (totalSlides < 1) return;

    // add dummy elements to ensure swiper behavior is always the same
    const swiperWrapper = this.dom.$swiperWrapper as HTMLElement;
    if (totalSlides < 4 && totalSlides > 1) {
      for (let i = 0; i < 4 - totalSlides; i++) {
        const emptySlide = document.createElement('div');
        emptySlide.setAttribute('aria-hidden', 'true');
        emptySlide.setAttribute('role', 'presentation');
        emptySlide.classList.add('product-images-carousel__slide');
        emptySlide.classList.add('swiper-slide');
        emptySlide.style.display = 'block';
        swiperWrapper.appendChild(emptySlide);
      }
    }

    this.swiperInstance = new Swiper(this.dom.$swiper as HTMLElement, {
      allowTouchMove: false,
      slidesPerView: 4,
      spaceBetween: 8,
      keyboard: {
        enabled: false
      },
      breakpoints: {
        1024: {
          spaceBetween: 16
        }
      }
    });

    this.swiperInstance.on('slideChangeTransitionEnd', this.handleSlideChangeEnd.bind(this));
    this.swiperInstance.on('click', this.handleSlideClick.bind(this));

    if (this.swiperInstance.slides.length > 4) {
      const prevBtn = this.dom.$prevBtn as HTMLElement;
      const nextBtn = this.dom.$nextBtn as HTMLElement;

      prevBtn.classList.remove('hidden');
      nextBtn.classList.remove('hidden');

      nextBtn.setAttribute('aria-disabled', 'false');
      nextBtn.removeAttribute('disabled');
    }

    this.setActiveSlide(0);
  }

  setActiveSlide(index: number) {
    const slides = [...(this.dom.$slides as NodeList)];
    const images = [...(this.dom.$images as NodeList)];

    slides.forEach((slide, i) => {
      (slide as HTMLElement).classList.toggle('swiper-slide--current', i === index);
    });

    images.forEach((image, i) => {
      (image as HTMLElement).classList.toggle('hidden', i !== index);
    });
  }
}

export default ProductImagesCarousel;
